import React, { useEffect, useMemo, useState, useCallback } from 'react';

// components
import { FormButton } from '../ui/buttons/FormButton';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as ArrowBottomIcon } from 'assets/icons/xpo/status_icons/arrow-bottom.svg';
import {
  ModalContainer,
  CloseButtonContainer,
  ModalBody,
  ModalFooter,
  ModalFormContainer,
  ModalHeader,
  ModalHeaderInnerContainer,
  ModalHeaderMainText,
  ModalHeaderSecondaryText,
  ModalRegularText,
  CheckboxLabel,
} from './modalComponents';
import ticketService from '../../ticketService';
import { SectionContainer } from '@ontruck/fe-ui';
import t from './TicketUpdateTranslations';
import useSWR from 'swr';
import getCarrierUserGroups from 'modules/emergencies/api/getCarrierUserGroups';
import getCarrierUserGroupsUsers from 'modules/emergencies/api/getCarrierUserGroupsUsers';
import BasicLoader from 'modules/emergencies/components/ui/loaders/basicLoader';
import styled from 'styled-components';
import CheckBoxLabel from '../ticketForm/components/CheckBoxLabel';

const LoaderContainer = styled.div`
  text-align: center;
  padding: 32px;
  width: 100%;
`;

const CollapsibleContainer = styled.div`
  padding: 12px 8px 12px 8px;
  gap: 4px;
  width: 100%;
  max-height: 250px;
  overflow: auto;
`;

const CollapsibleItem = styled.div`
  border-top: 1px solid var(--grey-500);
  &:last-of-type {
    border-bottom: 1px solid var(--grey-500);
  }
`;

const CollapsibleHeader = styled.div`
  padding: 12px 8px 12px 8px;
  gap: 4px;
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const CollapsibleLabel = styled.div`
  font-family: Roboto;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  display: inline-block;
  color: var(--accent-color-blue);
`;

const CarrierText = styled.div`
  font-family: Roboto;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;
  width: 100%;
  color: var(--grey700);
`;

const CarrierTextSmall = styled(CarrierText)`
  font-family: Roboto;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
`;

const CarrierSummaryContainer = styled.div`
  padding: 4px 0px 4px 0px;
  gap: 4px;
  border-radius: 4px 0px 0px 0px;
  opacity: 0px;
  background: var(--grey200);
  width: 100%;
  margin-top: 8px;
  height: 32px;
  display: flex;
  justify-content: center;
`;

const CarrierSummaryText = styled.div`
  font-family: Roboto;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  color: var(--accent-color-blue);
  text-align: center;
  text-decoration: underline;
`;

export const LaunchOffer = ({ ticket, ok, cancel }) => {
  const { data: segments, error: segmentsError } = useSWR(`segments/`, getCarrierUserGroups, {
    errorRetryCount: 0,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const isLoadingSegments = typeof segments === 'undefined' && !segmentsError;

  const [selectedSegmentIds, setSelectedSegmentIds] = useState([]);

  const segmentsByCategory = useMemo(() => {
    if (isLoadingSegments || typeof segments === 'undefined') return {};
    const groupedSegments = segments.results
      .filter((r) => r.parent !== null)
      .reduce((groups, segment) => {
        const name = segment.name;
        const category = segment?.parent?.name || 'Other';

        if (!groups[category]) {
          groups[category] = [];
        }

        //

        groups[category].push({ ...segment, name: name });

        return groups;
      }, {});
    return groupedSegments;
  }, [isLoadingSegments, segments]);

  const segmentsByCategoryKeys = useMemo(() => {
    return Object.keys(segmentsByCategory);
  }, [segmentsByCategory]);

  const [visibleCategory, setVisibleCategory] = useState(null);

  const onCategoryClick = (category) => {
    if (visibleCategory === category) {
      setVisibleCategory(null);
    } else {
      setVisibleCategory(category);
    }
  };

  const carriersBySegmentsKey = useMemo(() => {
    if (selectedSegmentIds.length === 0) return null;
    return ['carriersBySegment/', selectedSegmentIds.sort().join(',')];
  }, [selectedSegmentIds]);

  const { data: carriers, error: carriersError } = useSWR(
    carriersBySegmentsKey,
    getCarrierUserGroupsUsers,
    {
      errorRetryCount: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  useEffect(() => {
    if (typeof carriers !== 'undefined') {
      setNotSelectedCarrierIds([]);
    }
  }, [carriers]);

  const isLoadingCarriers = typeof carriers === 'undefined' && !carriersError;

  const [notSelectedCarrierIds, setNotSelectedCarrierIds] = useState([]);
  const [selectedCarrierIds, setSelectedCarrierIds] = useState([]);
  const [showCarriers, setShowCarriers] = useState(false);

  const getAvailableSelectedCarrierIds = useCallback(() => {
    const availableCarriersIds = carriers.results?.map((c) => c.id);
    return availableCarriersIds.filter((value) => !notSelectedCarrierIds.includes(value));
  }, [notSelectedCarrierIds, carriers]);

  useEffect(() => {
    if (isLoadingCarriers || typeof carriers === 'undefined') {
      return [];
    }

    const availableCarriersIds = getAvailableSelectedCarrierIds();
    setSelectedCarrierIds(availableCarriersIds);
  }, [isLoadingCarriers, carriers, getAvailableSelectedCarrierIds]);

  const onCarriersSummaryClick = () => {
    setShowCarriers(!showCarriers);
  };

  const onSegmentChange = (s) => {
    const set = new Set(selectedSegmentIds);
    if (set.has(s.id)) {
      set.delete(s.id);
    } else {
      set.add(s.id);
    }
    setSelectedSegmentIds(Array.from(set));
  };

  const formattedTicketId = useMemo(
    () => ticketService.utils.getFormatedId(ticket.id),
    [ticket.id]
  );

  const onCarrierChange = (c) => {
    const set = new Set(notSelectedCarrierIds);
    if (set.has(c.id)) {
      set.delete(c.id);
    } else {
      set.add(c.id);
    }
    setNotSelectedCarrierIds(Array.from(set));
  };

  const onSelectAllCarriersChange = (e) => {
    const isSelectAll = e.target.checked;

    if (isSelectAll) {
      const availableCarriersIds = getAvailableSelectedCarrierIds();
      setSelectedCarrierIds(availableCarriersIds);
      setNotSelectedCarrierIds([]);
    } else {
      setSelectedCarrierIds([]);
      setNotSelectedCarrierIds(carriers.results.map((c) => c.id));
    }
  };

  const hasCarriersError = useMemo(
    () => selectedCarrierIds.length === 0,
    [selectedCarrierIds.length]
  );

  const hasErrors = useMemo(() => hasCarriersError, [hasCarriersError]);

  const goBack = (e) => {
    e.preventDefault();
    cancel();
  };

  const onSubmit = () => {
    if (!hasErrors) {
      const dto = {
        carrier_user_ids: selectedCarrierIds,
      };
      ok(dto);
    }
  };
  return (
    <ModalContainer>
      <ModalBody>
        <ModalHeader>
          <ModalHeaderInnerContainer>
            <ModalHeaderSecondaryText>{`Ticket ID. ${formattedTicketId}`}</ModalHeaderSecondaryText>
            <ModalHeaderMainText>{t.launchOffer} </ModalHeaderMainText>
          </ModalHeaderInnerContainer>
        </ModalHeader>
        <ModalFormContainer>
          {/* Segments by group */}
          <SectionContainer>
            <ModalRegularText>{t.selectGroup}</ModalRegularText>
            {isLoadingSegments && (
              <LoaderContainer>
                <BasicLoader size={10} />
              </LoaderContainer>
            )}
            {!isLoadingSegments && (
              <CollapsibleContainer>
                {segmentsByCategoryKeys.map((category) => (
                  <CollapsibleItem key={category}>
                    <CollapsibleHeader onClick={() => onCategoryClick(category)}>
                      <CollapsibleLabel>{category}</CollapsibleLabel>
                      <ArrowBottomIcon />
                    </CollapsibleHeader>
                    {visibleCategory === category && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          width: '100%',
                          marginBottom: '16px',
                        }}
                      >
                        {segmentsByCategory[category].map((s) => (
                          <div style={{ width: '50%' }} key={s.id}>
                            <CheckBoxLabel backgroundVariant={'grey200'} style={{ margin: '2px' }}>
                              <input
                                type="checkbox"
                                checked={selectedSegmentIds.includes(s.id)}
                                onChange={() => {
                                  onSegmentChange(s);
                                }}
                              />
                              {s.name}
                            </CheckBoxLabel>
                          </div>
                        ))}
                        {segments.length === 0 && (
                          <>
                            <ModalRegularText>{t.noSegments}</ModalRegularText>
                          </>
                        )}
                      </div>
                    )}
                  </CollapsibleItem>
                ))}
              </CollapsibleContainer>
            )}
            {/* Carriers Summary */}
            {selectedSegmentIds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
                <div style={{ width: '50%' }}>
                  <ModalRegularText>{t.carrierSummary}</ModalRegularText>
                </div>
                <CarrierSummaryContainer onClick={onCarriersSummaryClick}>
                  {isLoadingCarriers && <BasicLoader size={10} />}
                  <CarrierSummaryText>
                    {!isLoadingCarriers && selectedSegmentIds.length === 0 && `0 ${t.carriers}`}
                    {!isLoadingCarriers &&
                      selectedSegmentIds.length > 0 &&
                      `${selectedCarrierIds.length} ${t.carriers}`}
                  </CarrierSummaryText>
                </CarrierSummaryContainer>
              </div>
            )}
            {/* Select/Unselect all */}
            {showCarriers ? (
              <CheckBoxLabel style={{ margin: '2px' }}>
                <input
                  type="checkbox"
                  checked={notSelectedCarrierIds.length === 0}
                  onChange={onSelectAllCarriersChange}
                />
                <CheckboxLabel
                  style={{ fontSize: '16px', color: 'var(--accent-color-blue)', fontWeight: '500' }}
                >
                  {t.selectAllCarriers}
                </CheckboxLabel>
              </CheckBoxLabel>
            ) : null}
            {/* Carriers */}
            {showCarriers && selectedSegmentIds.length > 0 && (
              <SectionContainer>
                {!isLoadingCarriers && (
                  <div
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    {carriers.results
                      .map((c) => {
                        const maxCharactersForName = 40;
                        const nameWithCarrier = `${c.carrier_stub?.name} - ${c.name}`;
                        const shortName =
                          nameWithCarrier.length > maxCharactersForName
                            ? nameWithCarrier.slice(0, maxCharactersForName) + '...'
                            : nameWithCarrier;
                        return { ...c, shortName };
                      })
                      .sort((a, b) => a.shortName.localeCompare(b.shortName))
                      .map((c) => {
                        return (
                          <div style={{ width: '100%' }} key={c.id}>
                            <CheckBoxLabel backgroundVariant={'grey200'} style={{ margin: '2px' }}>
                              <input
                                type="checkbox"
                                checked={selectedCarrierIds.includes(c.id)}
                                onChange={() => {
                                  onCarrierChange(c);
                                }}
                              />
                              <div>
                                <CarrierText>{c.shortName}</CarrierText>
                                <CarrierTextSmall>{c.email}</CarrierTextSmall>
                              </div>
                            </CheckBoxLabel>
                          </div>
                        );
                      })}
                    {carriers.results.length === 0 && (
                      <>
                        <ModalRegularText>{t.noCarriers}</ModalRegularText>
                      </>
                    )}
                  </div>
                )}
              </SectionContainer>
            )}
          </SectionContainer>
        </ModalFormContainer>
      </ModalBody>
      <ModalFooter>
        <FormButton variant="ghost" onClick={goBack}>
          {t.back}
        </FormButton>
        <FormButton isDisabled={isLoadingCarriers || hasErrors} onClick={onSubmit}>
          {t.launchOffer}
        </FormButton>
      </ModalFooter>
      <CloseButtonContainer onClick={goBack}>
        <CloseIcon />
      </CloseButtonContainer>
    </ModalContainer>
  );
};

export default LaunchOffer;
